import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { IonicVue } from '@ionic/vue'
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import App from './App.vue'
import router from './router'
import { initializeDatabase } from './services/storage/local-attendance-storage'
import { useSyncStore } from './stores/sync'
import { useLocaleStore } from './stores/locale'
import { useThemeStore } from './stores/theme'
import { useUserStore } from './stores/user'
import { useSettingsStore } from './stores/settings'
import connectivityService from './services/connectivity/connectivity'
import { refreshTokenIfNeeded } from './services/auth/auth-service'
import { i18n } from './services/i18n/i18n'
import { registerSW } from 'virtual:pwa-register'
import { logger } from './services/logger'
import appUpdateService from './services/update/app-update'

// Import polyfill ONLY for web platform to avoid breaking native functionality
if (Capacitor.getPlatform() === 'web') {
    // Dynamically import and initialize polyfill for web platform
    import('@capacitor-mlkit/barcode-scanning')
        .then(({ BarcodeScanner }) => {
            // Initialize the polyfill for web browsers
            if (BarcodeScanner && 'initializePolyfill' in BarcodeScanner) {
                ;(BarcodeScanner as any).initializePolyfill()
            }
        })
        .catch(error => {
            console.warn('[App] Failed to load barcode scanning polyfill:', error)
        })

    // Register service worker for PWA offline support
    registerSW({
        onNeedRefresh() {
            console.log('[PWA] New content available, reloading...')
        },
        onOfflineReady() {
            console.log('[PWA] App ready to work offline')
        },
        onRegisteredSW(swUrl, _registration) {
            console.log('[PWA] Service Worker registered:', swUrl)
        },
        onRegisterError(error) {
            console.error('[PWA] Service Worker registration error:', error)
        }
    })
}

/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/display.css';

/* Dark mode palette - Ionic recommended approach */
import '@ionic/vue/css/palettes/dark.class.css'

/* Leaflet CSS */
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'

/* Custom theme and global styles */
import '@/assets/styles/variables.css'
import '@/assets/styles/global.scss'
import '@/assets/styles/ionic-overrides.scss'

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
// Configure IonicVue with proper mode for consistent grid behavior
app.use(IonicVue, {
   // mode: 'md' // Force Material Design mode for consistent layout across platforms
})
app.use(router)
app.use(i18n)

// Initialize critical services before mounting
router.isReady().then(async () => {
    // After a bundle reset, CapacitorUpdater reloads the web view but native
    // plugins retain stale state, causing empty Dashboard. Detect the flag
    // set by the reset button and exit the app for a clean cold restart.
    if (localStorage.getItem('pending_app_restart')) {
        localStorage.removeItem('pending_app_restart')
        appUpdateService.initialize() // notifyAppReady — prevent rollback
        CapacitorApp.exitApp()
        return
    }

    // Notify updater early that the JS bundle loaded (prevents rollback if init is slow)
    appUpdateService.initialize()

    // Initialize logger service FIRST to ensure all logs are captured
    try {
        await logger.initialize()
        console.log('[App] Logger initialized successfully')
    } catch (error) {
        console.error('[App] Failed to initialize logger:', error)
        // Continue even if logger initialization fails
    }

    // Initialize theme BEFORE mounting to prevent flash of wrong theme
    try {
        const themeStore = useThemeStore()
        await themeStore.initializeTheme()
        console.log('[App] Theme initialized successfully')
    } catch (error) {
        console.error('[App] Failed to initialize theme:', error)
        // Continue even if theme initialization fails
    }

    // Initialize locale BEFORE mounting to prevent flash of wrong language
    try {
        const localeStore = useLocaleStore()
        await localeStore.initLocale()
        console.log('[App] Locale initialized successfully')
    } catch (error) {
        console.error('[App] Failed to initialize locale:', error)
    }

    // Initialize offline database BEFORE mounting to ensure data is available
    // when components mount (prevents empty dashboard after OTA update reload)
    try {
        await initializeDatabase()
        console.log('[App] Database initialized successfully')
    } catch (error) {
        console.error('[App] Failed to initialize offline database:', error)
        // App continues to run even if database initialization fails
    }

    // Load user profile and settings BEFORE mounting so Dashboard has data immediately
    // Without this, App.vue and Dashboard onMounted race each other after OTA reload
    try {
        const userStore = useUserStore()
        await userStore.loadProfile()
        console.log('[App] User profile loaded successfully')
    } catch (error) {
        console.error('[App] Failed to load user profile:', error)
    }

    try {
        const settingsStore = useSettingsStore()
        await settingsStore.loadCachedConfig()
        console.log('[App] Settings loaded from cache successfully')
    } catch (error) {
        console.error('[App] Failed to load cached settings:', error)
    }

    // Mount the app after all critical data is ready
    app.mount('#app')

    // Initialize network listener and sync after mount (non-blocking)
    const syncStore = useSyncStore()
    syncStore.initializeNetworkListener()
    syncStore.loadPendingCount().then(() => {
        if (!syncStore.isOffline && syncStore.pendingCount > 0) {
            console.log('[App] Initial sync triggered - found pending records while online')
            syncStore.syncPendingRecords()
        }
    })

    // Set up app state change listener for foreground resume
    CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
            // Re-sync status bar style in case system theme changed while backgrounded
            useThemeStore().applyTheme()

            // Refresh auth token if nearing expiry
            try {
                await refreshTokenIfNeeded()
            } catch (error) {
                console.error('[App] Failed to refresh token on resume:', error)
            }

            // Re-check connectivity status
            try {
                await connectivityService.initialize()
                const status = connectivityService.getStatus()
                syncStore.setIsOffline(!status.connected)

                // Trigger sync if online
                if (status.connected) {
                    syncStore.syncPendingRecords()
                }
            } catch (error) {
                console.error('[App] Failed to check connectivity on resume:', error)
            }

            // Check for app updates in background
            appUpdateService.checkForUpdates().catch(error => {
                console.warn('[App] Failed to check for updates on resume:', error)
            })
        }
    })
})
